/**
 * Settings hooks for fetching and updating app settings
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';

export interface CompanySettings {
  name: string;
  address: {
    street: string;
    postalCode: string;
    city: string;
    country: string;
  };
  tax_id: string;
  vat_id: string;
  bank_account: {
    bankName: string;
    iban: string;
    bic: string;
  };
}

export interface InvoiceSettings {
  default_payment_days: number;
  vat_rate: number;
  number_format: string;
}

export interface ReminderSettings {
  level_1_days: number;
  level_1_fee: number;
  level_2_days: number;
  level_2_fee: number;
  level_3_days: number;
  level_3_fee: number;
  interest_rate: number;
}

export interface Settings {
  company: CompanySettings;
  invoice: InvoiceSettings;
  reminder: ReminderSettings;
}

export function useSettings() {
  return useQuery({
    queryKey: ['settings'],
    queryFn: async () => {
      const response = await api.get<Settings>('/settings');
      return response;
    },
  });
}

export function useUpdateSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (settings: Partial<Settings>) => {
      const response = await api.patch<Settings>('/settings', settings);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
    },
  });
}
